import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { API_URL } from '../config';
import './AdminVitrinePage.css';

function AdminVitrinePage() {
  const navigate = useNavigate();
  const token = localStorage.getItem('authToken');

  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Création d'un nouveau thème
  const [creating, setCreating] = useState(false);
  const [newTitre, setNewTitre] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newPhotos, setNewPhotos] = useState([]);

  // Édition du titre/description d'un thème existant
  const [editingId, setEditingId] = useState(null);
  const [editTitre, setEditTitre] = useState('');
  const [editDescription, setEditDescription] = useState('');

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    loadThemes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadThemes = () => {
    setLoading(true);
    fetch(`${API_URL}/themes`)
      .then(res => res.json())
      .then(data => {
        setThemes(data);
        setLoading(false);
      })
      .catch(() => {
        setError("Impossible de charger la vitrine");
        setLoading(false);
      });
  };

  const authHeader = { 'Authorization': 'Bearer ' + token };

  // ---------- Création ----------

  const startCreating = () => {
    setCreating(true);
    setNewTitre('');
    setNewDescription('');
    setNewPhotos([]);
  };

  const cancelCreating = () => {
    setCreating(false);
    setNewTitre('');
    setNewDescription('');
    setNewPhotos([]);
  };

  const saveNewTheme = () => {
    if (!newTitre) return;
    setSaving(true);
    const formData = new FormData();
    formData.append('titre', newTitre);
    formData.append('description', newDescription);
    newPhotos.forEach(file => formData.append('photos', file));

    fetch(`${API_URL}/themes`, { method: 'POST', headers: authHeader, body: formData })
      .then(res => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then(() => {
        setSaving(false);
        cancelCreating();
        loadThemes();
      })
      .catch(() => {
        setSaving(false);
        setError("Erreur lors de la création du thème");
      });
  };

  // ---------- Édition titre/description ----------

  const startEditing = (theme) => {
    setEditingId(theme.id);
    setEditTitre(theme.titre || '');
    setEditDescription(theme.description || '');
  };

  const cancelEditing = () => {
    setEditingId(null);
  };

  const saveEdit = (theme) => {
    setSaving(true);
    const formData = new FormData();
    formData.append('titre', editTitre);
    formData.append('description', editDescription);

    fetch(`${API_URL}/themes/${theme.id}`, { method: 'PUT', headers: authHeader, body: formData })
      .then(res => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then(() => {
        setSaving(false);
        setEditingId(null);
        loadThemes();
      })
      .catch(() => {
        setSaving(false);
        setError("Erreur lors de la modification");
      });
  };

  // ---------- Ajout de photos à un thème existant ----------

  const addPhotosToTheme = (theme, files) => {
    if (!files || files.length === 0) return;
    setSaving(true);
    const formData = new FormData();
    formData.append('titre', theme.titre);
    formData.append('description', theme.description || '');
    Array.from(files).forEach(file => formData.append('photos', file));

    fetch(`${API_URL}/themes/${theme.id}`, { method: 'PUT', headers: authHeader, body: formData })
      .then(res => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then(() => {
        setSaving(false);
        loadThemes();
      })
      .catch(() => {
        setSaving(false);
        setError("Erreur lors de l'ajout des photos");
      });
  };

  const deletePhoto = (theme, filename) => {
    if (!window.confirm("Retirer cette photo ?")) return;
    fetch(`${API_URL}/themes/${theme.id}/photos?filename=${encodeURIComponent(filename)}`, {
      method: 'DELETE',
      headers: authHeader
    })
      .then(res => {
        if (!res.ok) throw new Error();
        loadThemes();
      })
      .catch(() => setError("Erreur lors de la suppression de la photo"));
  };

  const deleteTheme = (theme) => {
    if (!window.confirm(`Supprimer le thème "${theme.titre}" et toutes ses photos ?`)) return;
    fetch(`${API_URL}/themes/${theme.id}`, { method: 'DELETE', headers: authHeader })
      .then(res => {
        if (!res.ok) throw new Error();
        loadThemes();
      })
      .catch(() => setError("Erreur lors de la suppression du thème"));
  };

  if (loading) return <div className="vitrine-page"><p>Chargement...</p></div>;

  return (
    <div className="vitrine-page">
      <div className="vitrine-header">
        <h1>Vitrine (onglet Collections)</h1>
        <div className="dispo-header-links">
          <Link to="/admin/disponibilites" className="back-link">Disponibilités</Link>
          <Link to="/admin/gestion" className="back-link">Prestations & capsules</Link>
          <Link to="/admin" className="back-link">Calendrier des réservations</Link>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <p className="vitrine-hint">
        Chaque thème créé ici apparaît comme une carte dans l'onglet "Collections" de la page d'accueil du site, avec ses photos en carrousel.
      </p>

      {!creating && (
        <button className="add-button" onClick={startCreating}>+ Ajouter un thème</button>
      )}

      {creating && (
        <div className="theme-card editing">
          <input
            type="text" placeholder="Titre du thème (ex. Monster High)"
            value={newTitre}
            onChange={e => setNewTitre(e.target.value)}
          />
          <textarea
            placeholder="Description (optionnelle)"
            value={newDescription}
            onChange={e => setNewDescription(e.target.value)}
          />
          <input
            type="file" accept="image/*" multiple
            onChange={e => setNewPhotos(Array.from(e.target.files))}
          />
          {newPhotos.length > 0 && <p className="photos-count">{newPhotos.length} photo(s) sélectionnée(s)</p>}
          <div className="edit-actions">
            <button className="save-button" onClick={saveNewTheme} disabled={saving || !newTitre}>
              {saving ? 'Enregistrement...' : 'Créer le thème'}
            </button>
            <button className="cancel-button" onClick={cancelCreating}>Annuler</button>
          </div>
        </div>
      )}

      <div className="themes-list">
        {themes.map(theme => (
          <div className="theme-card" key={theme.id}>
            {editingId === theme.id ? (
              <>
                <input
                  type="text"
                  value={editTitre}
                  onChange={e => setEditTitre(e.target.value)}
                />
                <textarea
                  value={editDescription}
                  onChange={e => setEditDescription(e.target.value)}
                />
                <div className="edit-actions">
                  <button className="save-button" onClick={() => saveEdit(theme)} disabled={saving}>Enregistrer</button>
                  <button className="cancel-button" onClick={cancelEditing}>Annuler</button>
                </div>
              </>
            ) : (
              <div className="theme-header-row">
                <div>
                  <strong>{theme.titre}</strong>
                  {theme.description && <p className="theme-desc">{theme.description}</p>}
                </div>
                <div className="theme-actions">
                  <button onClick={() => startEditing(theme)}>Modifier</button>
                  <button className="delete-button" onClick={() => deleteTheme(theme)}>Supprimer le thème</button>
                </div>
              </div>
            )}

            <div className="photos-grid">
              {theme.photoFilenames.map(filename => (
                <div className="photo-thumb-wrap" key={filename}>
                  <img src={`${API_URL}/uploads/${filename}`} alt={theme.titre} className="photo-thumb" />
                  <button className="remove-photo-btn" onClick={() => deletePhoto(theme, filename)} title="Retirer cette photo">✕</button>
                </div>
              ))}

              <label className="add-photo-tile">
                + Ajouter
                <input
                  type="file" accept="image/*" multiple
                  style={{ display: 'none' }}
                  onChange={e => addPhotosToTheme(theme, e.target.files)}
                />
              </label>
            </div>
          </div>
        ))}
        {themes.length === 0 && !creating && <p className="empty-hint">Aucun thème pour l'instant.</p>}
      </div>
    </div>
  );
}

export default AdminVitrinePage;
